import React, { useState, useEffect } from 'react';
import ModelTrendChart from './ModelTrendChart';
import ModelComparisonChart from './ModelComparisonChart';
import QualityDistributionChart from './QualityDistributionChart';
import PerformanceHeatmap from './PerformanceHeatmap';

export default function VisualizationDashboard({ data }) {
  const [activeView, setActiveView] = useState("");

  const [selectedModel, setSelectedModel] = useState(null);

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (data && Object.keys(data.stats.models).length > 0 && !selectedModel) {
      setSelectedModel(Object.keys(data.stats.models)[0]);
    }
  }, [data, selectedModel]);

  if (!data) {
    return (
      <div className="text-center text-gray-500 py-8">
        No data available for visualization
      </div>
    );
  }

  // Get available models
  const availableModels = Object.keys(data.stats.models);

  const tabs = [
    { id: 'comparison', label: 'Model Comparison' },
    { id: 'trends', label: 'Performance Trends' },
    { id: 'distribution', label: 'Quality Distribution' },
    { id: 'heatmap', label: 'Performance Heatmap' },
  ];

  // Determine appropriate height based on view and expansion state
  const getVisualizationHeight = () => {
    return isExpanded ? 'calc(100vh - 200px)' : '550px';
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 mt-8 transition-all duration-300 ${isExpanded ? 'fixed inset-0 z-50 m-0 rounded-none overflow-auto' : ''
      }`}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-ktx-navy">Model Evaluation Visualization Dashboard</h2>

        <div className="flex space-x-2">
          {/* Toggle button when dashboard is closed */}
          {activeView === "" && (
            <button
              onClick={() => setActiveView("comparison")}
              className="px-4 py-2 bg-ktx-purple text-white rounded-md hover:bg-ktx-navy transition-colors"
            >
              View Charts
            </button>
          )}

          {/* Expand/collapse button when dashboard is open */}
          {activeView !== "" && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center"
              title={isExpanded ? "Exit fullscreen" : "Fullscreen mode"}
            >
              {isExpanded ? (
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Exit Fullscreen
                </span>
              ) : (
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                  </svg>
                  Fullscreen
                </span>
              )}
            </button>
          )}

          {/* Close button when dashboard is open */}
          {activeView !== "" && !isExpanded && (
            <button
              onClick={() => setActiveView("")}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Close Charts
            </button>
          )}
        </div>
      </div>

      {/* Dashboard is collapsed by default */}
      {activeView !== "" && (
        <>
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-1 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`
                    py-3 px-4 border-b-2 font-medium text-sm transition-colors flex items-center
                    ${activeView === tab.id
                      ? 'border-ktx-purple text-ktx-purple bg-light-purple/10'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}
                  `}
                >
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Model Selection (shown for all views except comparison) */}
          {activeView !== 'comparison' && activeView !== "" && availableModels.length > 0 && (
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Model:</label>
              <div className="flex flex-wrap gap-2">
                {availableModels.map(model => (
                  <button
                    key={model}
                    onClick={() => setSelectedModel(model)}
                    className={`px-4 py-2 rounded-md text-sm transition-colors ${selectedModel === model
                        ? 'bg-ktx-purple text-white'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                  >
                    {model}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="py-2">
            {/* Model Comparison Chart */}
            {activeView === 'comparison' && (
              <div style={{ height: getVisualizationHeight() }} className="transition-all duration-300">
                <ModelComparisonChart data={data} />
              </div>
            )}

            {/* Performance Trends Chart */}
            {activeView === 'trends' && selectedModel && (
              <div style={{ height: getVisualizationHeight() }} className="transition-all duration-300">
                <ModelTrendChart data={data} model={selectedModel} />
              </div>
            )}

            {/* Quality Distribution Chart */}
            {activeView === 'distribution' && selectedModel && (
              <div style={{ height: getVisualizationHeight() }} className="transition-all duration-300">
                <QualityDistributionChart data={data} model={selectedModel} />
              </div>
            )}

            {/* Performance Heatmap */}
            {activeView === 'heatmap' && selectedModel && (
              <div style={{ height: getVisualizationHeight() }} className="transition-all duration-300 overflow-visible">
                <PerformanceHeatmap data={data} model={selectedModel} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}